import { ORDER_TYPES } from './Constants';

/**
 * Validates if the current order selection is valid for cart operations
 * @param {Object} formValues - Current checkout form values
 * @param {Object} hotel - Hotel data
 * @param {string} tableId - Table ID (for dine-in orders)
 * @param {Object} timeServices - Time services hook data
 * @param {Object} scheduledSlots - Scheduled slots hook data
 * @param {string} selectedOrderType - Currently selected order type
 * @returns {boolean} - Whether the order selection is valid
 */
export const isOrderSelectionValid = (formValues, hotel, tableId, timeServices, scheduledSlots, selectedOrderType) => {
  if (!selectedOrderType) return false;
  
  // For table orders, only check if orderType is selected
  if (tableId) {
    return formValues?.orderType != null;
  }

  // For delivery/pickup orders, check if order type and delivery time are selected
  if (!formValues?.orderType || !formValues?.deliveryTime) {
    return false;
  }

  // Check if the selected order type has available slots
  const { isAsapAvailable } = timeServices || {};
  const { isScheduledAvailable, getAvailableDates, isOrderTypeAvailableForDate } = scheduledSlots || {};

  if (!isAsapAvailable || !isScheduledAvailable) {
    return false;
  }

  // Check ASAP availability
  const asapAvailable = isAsapAvailable(selectedOrderType);
  
  // Check scheduled availability
  let scheduledAvailable = false;
  if (isScheduledAvailable && selectedOrderType) {
    const dates = getAvailableDates;
    if (dates && dates.length > 0) {
      // Check if any of the available dates have valid time slots for this order type
      for (const date of dates) {
        if (isOrderTypeAvailableForDate && isOrderTypeAvailableForDate(date.date, selectedOrderType)) {
          scheduledAvailable = true;
          break;
        }
      }
    }
  }

  // Order selection is valid if either ASAP or scheduled is available
  return asapAvailable || scheduledAvailable;
};

/**
 * Checks if cart buttons should be disabled
 * @param {Object} formValues - Current checkout form values
 * @param {Object} hotel - Hotel data
 * @param {string} tableId - Table ID (for dine-in orders)
 * @param {Object} timeServices - Time services hook data
 * @param {Object} scheduledSlots - Scheduled slots hook data
 * @param {boolean} hotelStatus - Hotel active status
 * @param {boolean} isTodayHoliday - Whether today is a holiday
 * @param {string} selectedOrderType - Currently selected order type
 * @returns {boolean} - Whether cart buttons should be disabled
 */
export const shouldDisableCartButtons = (formValues, hotel, tableId, timeServices, scheduledSlots, hotelStatus, isTodayHoliday, selectedOrderType) => {
  // Disable if hotel is not active or it's a holiday
  if (!hotelStatus || isTodayHoliday) {
    return true;
  }

  // Disable if order selection is not valid
  const isValid = isOrderSelectionValid(formValues, hotel, tableId, timeServices, scheduledSlots, selectedOrderType);
  return !isValid;
};
